import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pairDevice, heartbeat } from '../services/kioskService';

/**
 * 共用端末のペアリング画面。
 *
 * 管理画面で端末を登録すると6桁のコードが出るので、それをこの端末で打ち込む。
 * 成功すると端末トークンが Cookie に入り、以後この端末は打刻専用になる。
 *
 * **ログインを求めない。** 店に置く端末に管理者のアカウントでログインさせると、
 * その端末から全社の勤怠が見えてしまう。それを避けるための仕組みなので、
 * ここでログインを挟んだら設計の意味が消える。
 */
const KioskSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // 既に登録済みならそのまま打刻画面へ
  useEffect(() => {
    heartbeat()
      .then(() => navigate('/kiosk', { replace: true }))
      .catch(() => { /* 未登録。この画面に留まる */ });
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      await pairDevice(code);
      navigate('/kiosk', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || '登録に失敗しました');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.root}>
      <form onSubmit={submit} style={styles.card}>
        <h1 style={styles.title}>打刻用共用端末の登録</h1>
        <p style={styles.lead}>
          管理画面の「打刻用共用端末」で発行した6桁のコードを入力してください。
        </p>

        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          // 端末にキーボードが無いことが多い。数字キーパッドを出す
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          autoFocus
          style={styles.input}
        />

        {error && <p style={styles.error}>{error}</p>}

        <button type="submit" disabled={code.length !== 6 || busy} style={{
          ...styles.button,
          opacity: code.length !== 6 || busy ? 0.5 : 1,
          cursor: code.length !== 6 || busy ? 'not-allowed' : 'pointer',
        }}>
          {busy ? '登録中…' : 'この端末を登録する'}
        </button>

        <p style={styles.note}>
          コードの有効期限は発行から30分です。切れている場合は管理画面で再発行してください。
        </p>

        {/*
          設置した人が後で困る一番の原因がこれ。
          Cookie を消すと登録が外れ、朝の忙しい時間に「打刻できない」で発覚する。
          登録する時点で目に入る場所に書いておく。
        */}
        <p style={styles.caution}>
          ⚠️ この登録は、この端末のブラウザに保存されます。
          <strong>Cookie を削除すると登録が外れ、コードの再発行と再入力が必要になります。</strong>
          「終了時に Cookie を削除する」設定は切っておき、
          プライベート／シークレットモードでは開かないでください。
        </p>
      </form>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // ダッシュボード・打刻画面と同じ基調（白カード + 淡い地色）に揃える。
  // 設置作業からそのまま打刻画面へ入るので、ここだけ配色が変わると分断される。
  root: {
    minHeight: '100vh',
    backgroundColor: '#f6f8fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    backgroundColor: 'white',
    color: '#24292e',
    borderRadius: 8,
    border: '1px solid #d0d7de',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: 'clamp(24px, 6vw, 36px)',
    width: 'min(92vw, 440px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  title: { margin: 0, fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: 600, color: '#24292e' },
  lead: { margin: 0, color: '#6c757d', fontSize: 'clamp(13px, 2.8vw, 14px)', lineHeight: 1.8 },
  input: {
    // 遠目でも入力が確認できるように大きく、字間を空ける
    fontSize: 'clamp(32px, 9vw, 44px)',
    letterSpacing: '0.35em',
    textAlign: 'center',
    padding: '14px 8px',
    borderRadius: 6,
    border: '1px solid #d0d7de',
    backgroundColor: 'white',
    color: '#24292e',
    fontFamily: 'inherit',
    fontWeight: 300,
  },
  button: {
    padding: '16px',
    fontSize: 'clamp(15px, 3.5vw, 17px)',
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#2ea44f',
    border: 'none',
    borderRadius: 6,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  },
  error: {
    margin: 0,
    color: '#cf222e',
    backgroundColor: '#ffebe9',
    border: '1px solid #ffcecb',
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: 14,
  },
  note: { margin: 0, color: '#8b949e', fontSize: 12, lineHeight: 1.8 },
  caution: {
    margin: 0,
    color: '#9a6700',
    backgroundColor: '#fff8c5',
    border: '1px solid #f0e68c',
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: 12,
    lineHeight: 1.8,
  },
};

export default KioskSetupPage;
